/**
 * /gl — General Ledger explorer
 *
 * Top toolbar: date range, account_code lookup, source/status/type filters,
 * search box, draft toggle. All controlled state. URL parameters intentionally
 * NOT wired yet — keeps the first cut focused on shape.
 *
 * Below: GLGrid (DataGridPro, server-side paginated) and a totals footer.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  glLinesQueryOptions,
  glSummaryQueryOptions,
  organizationCurrentQueryOptions,
  type GLFilters,
} from "@/lib/queries";
import { GLGrid } from "@/grids/GLGrid";
import { JournalDetailDialog } from "@/components/JournalDetailDialog";

export const Route = createFileRoute("/_auth/gl")({
  component: GLPage,
});

const ACCOUNT_TYPES = ["asset", "liability", "equity", "revenue", "expense"] as const;
const STATUSES = ["draft", "posted", "reversed"] as const;
const SOURCES = [
  "manual", "ap_invoice", "ar_invoice", "payment",
  "depreciation", "fx_reval", "period_close",
  "wip_journal", "progress_billing", "job_cost", "money_fact",
  "bank_rec", "asset_acquisition",
] as const;

function GLPage() {
  // Journal detail dialog (opens when journal_no is clicked)
  const [openJournalId, setOpenJournalId] = useState<string | null>(null);

  // Filter state
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [accountCode, setAccountCode] = useState<string>("");
  const [accountType, setAccountType] = useState<string>("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [includeDrafts, setIncludeDrafts] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const filters = useMemo<GLFilters>(
    () => ({
      from_date: fromDate || null,
      to_date: toDate || null,
      account_code: accountCode || null,
      account_type: accountType || null,
      source: sourceFilter || null,
      status: statusFilter || null,
      search: search || null,
      include_drafts: includeDrafts,
      limit: pageSize,
      offset: page * pageSize,
      sort: "journal_date_desc",
    }),
    [
      fromDate, toDate, accountCode, accountType,
      sourceFilter, statusFilter, search, includeDrafts,
      page, pageSize,
    ],
  );

  const linesQuery = useQuery(glLinesQueryOptions(filters));
  const organizationQuery = useQuery(organizationCurrentQueryOptions());
  const baseCurrency = organizationQuery.data?.base_currency_code ?? "USD";
  const showBalanceControl = !accountCode && !accountType && !search;

  // Summary uses the exact same filter envelope (minus pagination), so footer
  // totals and server-side row count always describe the visible dataset.
  const summaryQuery = useQuery(
    glSummaryQueryOptions({
      from_date: fromDate || null,
      to_date: toDate || null,
      account_code: accountCode || null,
      account_type: accountType || null,
      source: sourceFilter || null,
      status: statusFilter || null,
      search: search || null,
      include_drafts: includeDrafts,
    }),
  );

  // The summary shares the row filters, so its count is the exact server-side
  // pagination total rather than a page-length approximation.
  const totalRows = summaryQuery.data?.line_count ?? linesQuery.data?.length ?? 0;

  const rows = linesQuery.data ?? [];

  function reset() {
    setFromDate(""); setToDate(""); setAccountCode("");
    setAccountType(""); setSourceFilter(""); setStatusFilter("");
    setSearch(""); setIncludeDrafts(false);
    setPage(0);
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">General Ledger</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            One row per journal line · DB-validated balanced &amp; immutable
          </span>
          <div className="flex-1" />
          {linesQuery.isFetching && (
            <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
          )}
          <Link
            to="/journals/new"
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
            title="Create a manual journal entry"
          >
            + New journal
          </Link>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex flex-wrap items-end gap-3 text-sm dark:bg-gray-900 dark:border-gray-800">
        <Field label="From">
          <input
            type="date" value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm dark:border-gray-700"
          />
        </Field>
        <Field label="To">
          <input
            type="date" value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm dark:border-gray-700"
          />
        </Field>
        <Field label="Account code">
          <input
            type="text" value={accountCode} placeholder="e.g. 4050"
            onChange={(e) => { setAccountCode(e.target.value.trim()); setPage(0); }}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm w-28 font-mono dark:border-gray-700"
          />
        </Field>
        <Field label="Type">
          <select
            value={accountType}
            onChange={(e) => { setAccountType(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">all</option>
            {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">all</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Source">
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">all</option>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Search memo">
          <input
            type="text" value={search} placeholder="memo or description"
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm w-56 dark:border-gray-700"
          />
        </Field>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-1 dark:text-slate-300">
          <input
            type="checkbox" checked={includeDrafts}
            onChange={(e) => { setIncludeDrafts(e.target.checked); setPage(0); }}
          />
          include drafts
        </label>
        <button
          onClick={reset}
          className="text-xs text-slate-500 hover:text-slate-900 underline ml-2 dark:text-slate-400"
        >
          reset
        </button>
      </div>

      {/* Grid + footer */}
      <div className="flex-1 px-6 py-4 overflow-hidden">
        <GLGrid
          rows={rows}
          summary={summaryQuery.data}
          total={totalRows}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setPageSize(n); setPage(0); }}
          loading={linesQuery.isLoading}
          baseCurrency={baseCurrency}
          showBalanceControl={showBalanceControl}
          onJournalClick={(id) => setOpenJournalId(id)}
        />
        {linesQuery.error && (
          <div className="mt-2 text-sm text-red-600">
            Error: {(linesQuery.error as Error).message}
          </div>
        )}
      </div>

      <JournalDetailDialog
        open={openJournalId != null}
        journalId={openJournalId}
        onClose={() => setOpenJournalId(null)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
        {label}
      </span>
      {children}
    </div>
  );
}
